import { useEffect, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Returns one data point per session that included this exercise:
// { date, sessionId, weight_kg, reps } where weight_kg is the heaviest
// non-warmup set of that session. Sorted ascending by date — ready for
// charting. Also returns flat `allSets` for PR detection.
export function useExerciseProgression(exerciseId) {
  const { user } = useAuth()
  const [points, setPoints] = useState([])
  const [allSets, setAllSets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!supabaseConfigured || !user || !exerciseId) {
      setPoints([])
      setAllSets([])
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      const { data, error: err } = await supabase
        .from('exercise_logs')
        .select(`
          id, session_id,
          sessions!inner(id, date),
          sets(weight_kg, reps, is_warmup)
        `)
        .eq('exercise_id', exerciseId)
      if (cancelled) return
      if (err) {
        setError(err.message)
        setPoints([])
        setAllSets([])
        setLoading(false)
        return
      }
      const rows = (data || [])
        .map((log) => {
          const working = (log.sets || []).filter((s) => !s.is_warmup)
          if (working.length === 0) return null
          const top = working.reduce(
            (a, b) => (Number(b.weight_kg) > Number(a.weight_kg) ? b : a)
          )
          return {
            sessionId: log.sessions?.id,
            date: log.sessions?.date,
            weight_kg: Number(top.weight_kg),
            reps: Number(top.reps),
          }
        })
        .filter(Boolean)
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      const flat = (data || []).flatMap((log) =>
        (log.sets || []).map((s) => ({
          weight_kg: Number(s.weight_kg),
          reps: Number(s.reps),
          is_warmup: !!s.is_warmup,
        }))
      )
      setPoints(rows)
      setAllSets(flat)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [exerciseId, user])

  return { points, allSets, loading, error }
}
